import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";
import {
  usePublicIdentityConflicts,
  usePublicSumsubVerificationPreviousAnswer,
  usePublicSumsubVerificationValue,
  useSavePublicSumsubVerification,
} from "@/lib/hooks/use-public-flow/use-sumsub-verification";
import { log } from "@/lib/logger/browser-logger";
import { SumsubVerificationWidget } from "@/pages/clients/detail/flows/instance/sumsub-verification/sumsub-verification-widget";
import { usePublicFlowActions } from "../public-flow-context";
import type { ComponentStyleType } from "./types";

type PublicSumsubVerificationRendererProps = {
  component: { id: string; config?: string };
  isCompleted: boolean;
  language: string;
  hasValidationError?: boolean;
};

export function SumsubVerificationRenderer({
  component,
  isCompleted,
  language,
  hasValidationError,
}: PublicSumsubVerificationRendererProps) {
  const actions = usePublicFlowActions();
  const queryClient = useQueryClient();
  const {
    inputData,
    calculatedData,
    isLoading: valueLoading,
    refetch,
  } = usePublicSumsubVerificationValue();
  const { previousAnswer, isLoading: prevLoading } = usePublicSumsubVerificationPreviousAnswer();
  const save = useSavePublicSumsubVerification();
  // QL-f — the client answers document/record conflicts here, with no advisor
  // present. Same two answers as the advisor page; the difference is only that
  // the resulting change is logged with no author.
  const conflicts = usePublicIdentityConflicts(calculatedData);

  let config: {
    style?: string;
    title?: string;
    description?: string;
  } = {};

  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // Mirror the verification status into the hidden flow field whenever the
  // saved snapshot becomes "checked". Fires for both the polled webhook
  // result and the "Use Previous Answer" mutation, since both update
  // calculatedData via react-query.
  const lastSyncedAt = useRef<string | null>(null);
  useEffect(() => {
    if (!calculatedData) return;
    try {
      const calculated = JSON.parse(calculatedData);
      if (!calculated.hasBeenChecked) return;
      // De-dupe: only re-emit when the snapshot actually changes
      const key =
        String(calculated.searchedAt ?? "") + String(calculated.status?.reviewAnswer ?? "");
      if (lastSyncedAt.current === key) return;
      lastSyncedAt.current = key;
      actions.setFieldValue("__sumsub_verified__", calculated.status?.isVerified ? "yes" : "no");
      // QL-68 — the document's alpha-3 issuing country drives its own rules.
      // Always written, "" when the checked snapshot carries no country: the
      // store keeps the last value, so a later snapshot without one ("Use
      // Previous Answer", a re-check Sumsub reports countryless) must clear
      // it, matching what the engines derive from the same snapshot.
      actions.setFieldValue(
        "__sumsub_document_country__",
        typeof calculated.info?.country === "string" ? calculated.info.country : "",
      );
      // P5 (§12.C) — an async/polled verification result changes completion;
      // refresh the preflight so the progress bar / markers update.
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    } catch {
      /* ignore */
    }
  }, [calculatedData, actions, queryClient]);

  const handleRefetchValue = async () => {
    const result = await refetch();
    return result.data?.calculatedData ?? null;
  };

  const handleUsePreviousAnswer = previousAnswer
    ? async () => {
        try {
          const calc = JSON.parse(previousAnswer.calculatedData);
          const inp = previousAnswer.inputData ? JSON.parse(previousAnswer.inputData) : {};
          await save.mutateAsync({ ...inp, ...calc });
        } catch (error) {
          log.error({ err: error }, "Failed to use previous sumsub answer");
          throw error;
        }
      }
    : undefined;

  if (valueLoading || prevLoading) return null;

  return (
    <SumsubVerificationWidget
      componentId={component.id}
      style={(config.style as ComponentStyleType) || "none"}
      title={config.title}
      description={config.description}
      language={language}
      inputData={inputData}
      calculatedData={calculatedData}
      onRefetchValue={handleRefetchValue}
      disabled={isCompleted}
      previousAnswer={previousAnswer}
      onUsePreviousAnswer={handleUsePreviousAnswer}
      accessTokenUrl="/public/flow/sumsub-verification/access-token"
      hasValidationError={hasValidationError}
      identityConflicts={conflicts.rows}
      onResolveIdentityConflict={conflicts.resolve}
    />
  );
}
